<template>
  <div
    class="flex h-16 items-center space-x-4"
    :class="{ 'bg-outer-space-900 pr-4': variant === 'goLive' }"
  >
    <!-- Icon (hidden in goLive) -->
    <div
      class="flex-shrink-0"
      :class="{ hidden: variant === 'goLive' }"
      style="order: 0"
    >
      <Icon size="1.5em" name="lucide:puzzle" />
    </div>

    <UiSwitch
      :checked="destination.enabled"
      :variant="variant === 'goLive' ? 'background' : 'default'"
      size="lg"
      @update:checked="onToggle"
      :style="{ order: variant === 'goLive' ? 0 : 3 }"
    />

    <!-- Nickname text -->
    <div class="flex-grow truncate" :style="{ order: 1 }">
      <div class="truncate">{{ destination.nickname }}</div>
    </div>

    <!-- Settings button -->
    <DialogEditDestination
      :stream-key="destination.streamKey"
      :nickname="destination.nickname"
      :url="destination.streamUrl"
      @save="onSave"
    >
      <UiDialogTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          condensed
          :style="{ order: variant === 'goLive' ? 2 : 1 }"
        >
          <IconSettings class="h-5 w-5 opacity-70" />
        </Button>
      </UiDialogTrigger>
    </DialogEditDestination>

    <!-- Delete button -->
    <UiAlertDialog>
      <UiAlertDialogTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          condensed
          :style="{ order: variant === 'goLive' ? 3 : 2 }"
        >
          <IconTrash class="h-5 w-5 opacity-70" />
        </Button>
      </UiAlertDialogTrigger>
      <UiAlertDialogContent>
        <UiAlertDialogHeader>
          <UiAlertDialogTitle>Are you absolutely sure?</UiAlertDialogTitle>
          <UiAlertDialogDescription>
            This action cannot be undone. This will permanently delete your
            account and remove your data from our servers.
          </UiAlertDialogDescription>
        </UiAlertDialogHeader>
        <UiAlertDialogFooter>
          <UiAlertDialogCancel>Cancel</UiAlertDialogCancel>
          <UiAlertDialogAction @click="deleteTarget"
            >Continue</UiAlertDialogAction
          >
        </UiAlertDialogFooter>
      </UiAlertDialogContent>
    </UiAlertDialog>
  </div>
</template>

<script setup lang="ts">
import { updateRstUserTarget, deleteRstUserTarget } from '~/lib/streamlabs-api';

const api = useApiStore();
const multistream = useMultistreamStore();
const auth = useAuthStore();

const props = defineProps<{
  destination: DestinationRelay;
  variant?: 'default' | 'goLive';
}>();

const onSave = async ({
  nickname,
  url,
  streamKey,
}: {
  nickname: string;
  url: string;
  streamKey: string;
}) => {
  if (!api.multistream.user.targets.data) {
    return;
  }

  const index = api.multistream.user.targets.data.findIndex((item) => {
    return item.id === props.destination.relay_id;
  });

  if (index < 0 || !api.multistream.user.targets.data[index]) {
    return;
  }

  api.multistream.user.targets.data[index].label = nickname;
  api.multistream.user.targets.data[index].streamKey =
    url + (streamKey.length ? `/${streamKey}` : '');

  await updateRstUserTarget(api.multistream.user.targets.data[index]).catch(
    async (err) => {
      console.error(err);
      await api.multistream.user.targets.refresh();
    },
  );
};

const deleteTarget = async () => {
  if (!api.multistream.user.targets.data) {
    return;
  }

  const index = api.multistream.user.targets.data.findIndex((item) => {
    return item.id === props.destination.relay_id;
  });

  if (index < 0) {
    return;
  }

  const spliced = api.multistream.user.targets.data.splice(index, 1);

  if (!spliced[0]) return;
  await deleteRstUserTarget(spliced[0]).catch(async (err) => {
    console.error(err);
    await api.multistream.user.targets.refresh();
  });
};

const onToggle = async (checked: boolean) => {
  if (!api.multistream.user.targets.data) {
    return;
  }

  if (
    checked === true &&
    multistream.enabledDestinations.length >= 1 &&
    !auth.ensureUltra('multistream_toggle', {
      refl: 'obsplugin-multistream',
    })
  ) {
    return;
  }

  const index = api.multistream.user.targets.data.findIndex((item) => {
    return item.id === props.destination.relay_id;
  });

  if (index < 0 || !api.multistream.user.targets.data[index]) {
    return;
  }

  api.multistream.user.targets.data[index].enabled = checked;

  await updateRstUserTarget(api.multistream.user.targets.data[index]).catch(
    async (err) => {
      console.error(err);
      await api.multistream.user.targets.refresh();
    },
  );
};
</script>
